import {
  API_URL,
  getAccessToken,
  getActingOrganizationId,
} from "@/api/_client";
import { ChatMessageType } from "@/api/chat";
import { useUser } from "@/api/user";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
  AgentContext,
  AgentViewDescription,
  OutgoingAgentMessage,
} from "./agent-provider";
import { useAppContext } from "./app-context";
import { useNavigate } from "react-router-dom";

const AGENT_WEBSOCKET_URL = API_URL + "ws/agent";

export const AgentProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentChatSessionId, chatOrganizationId } = useAppContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [usersTyping, setUsersTyping] = React.useState<
    { first_name: string; last_name: string; id: string }[]
  >([]);
  const user = useUser();
  const [shouldConnect, setShouldConnect] = React.useState(false);

  const [messages, setMessages] = React.useState<ChatMessageType[]>([]);
  const typingTimeouts = React.useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [currentViewDescription, setCurrentViewDescription] =
    React.useState<AgentViewDescription>(null);
  const processedNavigations = React.useRef<Set<string>>(new Set());
  const activeDashboardIdRef = React.useRef<string | null>(null);

  // Clear agent messages when navigating to a different resource (e.g.,
  // switching dashboards). Uses a stable resource key (dashboard_id,
  // project_id) so toggling between widget-level and dashboard-level context
  // within the same resource does NOT clear messages — the conversation
  // history stays visible.
  const prevNavigationKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const vars = currentViewDescription?.known_vars;
    // Use the primary resource ID as the stable key. Widget context adds
    // widget_id but keeps the same dashboard_id, so transitions like
    // widget→dashboard after an escalation response won't wipe history.
    const navigationKey = vars
      ? (vars.dashboard_id as string) ??
        (vars.project_id as string) ??
        JSON.stringify(vars)
      : null;
    if (
      prevNavigationKeyRef.current !== null &&
      navigationKey !== prevNavigationKeyRef.current
    ) {
      setMessages([]);
      processedNavigations.current.clear();
    }
    prevNavigationKeyRef.current = navigationKey;
    activeDashboardIdRef.current =
      (vars?.dashboard_id as string) ?? null;
  }, [currentViewDescription]);

  const TYPING_TIMEOUT_DURATION = 60000 * 5;

  const removeUserFromTyping = useCallback((userId: string) => {
    const existingTimeout = typingTimeouts.current.get(userId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      typingTimeouts.current.delete(userId);
    }
    setUsersTyping((oldUsers) => oldUsers.filter((user) => user.id !== userId));
  }, []);

  const token = getAccessToken();
  const url = new URL(AGENT_WEBSOCKET_URL);
  url.searchParams.append("token", token || "");
  // Admin home (TOP_HOST) has no cookie-based acting org — callers set
  // chatOrganizationId via AppContext to nominate which org the agent
  // should act as. Backend membership is validated at the WS handshake
  // (authentication_class.py), so a user can't chat with an org they
  // don't belong to.
  const orgId = chatOrganizationId || getActingOrganizationId();
  url.searchParams.append("organization_id", orgId || "");

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    url.href,
    {
      shouldReconnect: () => true,
    },
    shouldConnect,
  );

  const submitEvent = useCallback(
    (event: string | object) => {
      if (currentChatSessionId && token) {
        sendJsonMessage({ event: event });
      }
    },
    [currentChatSessionId, token, sendJsonMessage],
  );

  React.useEffect(() => {
    if (!currentChatSessionId) return;
    // Cleanup typing timeouts when chat session changes
    const timeouts = typingTimeouts.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      setUsersTyping([]);
    };
  }, [currentChatSessionId]);

  const canSendMessages = readyState === ReadyState.OPEN;
  const canSendMessagesRef = React.useRef(canSendMessages);

  React.useEffect(() => {
    canSendMessagesRef.current = canSendMessages;
  }, [canSendMessages]);

  React.useEffect(() => {
    if (!lastMessage || !lastMessage.data) return;
    // Parse the last message data and handle different message types
    const messageData = JSON.parse(lastMessage.data);
    // if the user is "typing" and just received a message, clear their typing status
    if (messageData.type === "chat_message") {
      removeUserFromTyping(messageData.user?.id);
    }
    if (
      messageData.sender === "event" &&
      "is_typing" in (messageData.events || {})
    ) {
      // Handle typing events
      if (messageData.events.is_typing) {
        if (messageData.user?.id === user.data?.id) {
          // If the user is typing, we don't want to add them to the typing list
          return;
        }

        // Clear any existing timeout for this user
        const existingTimeout = typingTimeouts.current.get(
          messageData.user?.id,
        );
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        setUsersTyping((oldUsers) => {
          if (!oldUsers.map((ou) => ou.id).includes(messageData.user?.id)) {
            return [...oldUsers, messageData.user];
          }
          return oldUsers;
        });

        const timeout = setTimeout(() => {
          removeUserFromTyping(messageData.user?.id);
        }, TYPING_TIMEOUT_DURATION);

        typingTimeouts.current.set(messageData.user?.id, timeout);
      } else {
        removeUserFromTyping(messageData.user?.id);
      }
      return;
    }
    // Drop messages scoped to a different dashboard (stale in-flight responses)
    if (
      messageData.scope_dashboard_id &&
      activeDashboardIdRef.current &&
      messageData.scope_dashboard_id !== activeDashboardIdRef.current
    ) {
      return;
    }
    // handle messages
    setMessages((oldMessages) => {
      if (oldMessages === undefined) return [messageData];
      const messageExists = oldMessages.some(
        (msg) => msg.id && messageData.id && msg.id === messageData.id,
      );
      if (messageExists) return oldMessages;
      return [...oldMessages, messageData];
    });
    if (
      messageData.type === "event" &&
      (messageData.events.users_added?.length ||
        messageData.events.users_removed?.length)
    ) {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
    if (messageData.cache_invalidations) {
      messageData.cache_invalidations.forEach((key: string[]) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
    if (messageData.force_navigation && messageData.id) {
      // Only navigate if we haven't already processed this message's navigation
      if (!processedNavigations.current.has(messageData.id)) {
        processedNavigations.current.add(messageData.id);
        console.log("Forcing navigation to ", messageData.force_navigation);
        let path = messageData.force_navigation;
        if (!path.startsWith("/")) {
          path = "/" + path;
        }
        path = path.replace(/^\/+/, "/");
        navigate(path);
      }
    }
  }, [
    lastMessage,
    queryClient,
    user.data?.id,
    removeUserFromTyping,
    TYPING_TIMEOUT_DURATION,
    navigate,
  ]);

  // Extract suggested replies from the latest AI message, but only if
  // no user message has been sent after it (i.e. the user hasn't answered yet)
  const suggestedReplies = React.useMemo(() => {
    if (messages.length === 0) return [];
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender !== "ai") return [];
    return lastMessage.suggested_replies || [];
  }, [messages]);

  const sendMessage = useCallback(
    ({ message, attachments = [], context, silent, handoffMessage }: OutgoingAgentMessage) => {
      // Use passed context if provided, otherwise fall back to currentViewDescription
      const effectiveContext = context ?? currentViewDescription;

      if (!shouldConnect) {
        setShouldConnect(true);
      }

      // Inject a visible handoff marker before the silent message
      if (handoffMessage) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "event",
            content: handoffMessage,
            user: { id: "", first_name: "", last_name: "", email: "" },
            sources: [],
          },
        ]);
      }

      const history = messages.map((msg) => ({
        sender: msg.sender,
        content: msg.content,
        attachments: msg.attachments || [],
      }));
      const payload: Record<string, unknown> = {
        message,
        history,
        context: effectiveContext,
      };
      if (attachments.length) {
        payload.attachments = attachments.map((a) => ({
          filename: a.filename,
          mime_type: a.mime_type,
          data: a.data,
          size: a.size,
        }));
      }
      if (silent) payload.silent = true;

      if (canSendMessagesRef.current) {
        sendJsonMessage(payload);
        return;
      }

      let attempts = 0;
      const maxAttempts = 20;

      const interval = setInterval(() => {
        attempts++;
        if (canSendMessagesRef.current) {
          sendJsonMessage(payload);
          clearInterval(interval);
        } else {
          if (attempts >= maxAttempts) {
            clearInterval(interval);
          }
        }
      }, 500);

      return () => clearInterval(interval);
    },
    [shouldConnect, messages, currentViewDescription, sendJsonMessage],
  );

  return (
    <AgentContext.Provider
      value={{
        canSendMessages,
        sendMessage,
        messages,
        submitEvent,
        usersTyping,
        setCurrentViewDescription,
        suggestedReplies,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};
